import React, { useEffect, useState } from "react";
import store from "../store";

import BaseService from "services/BaseService"; // your axios instance
import { onSignOutSuccess } from "../store/auth/sessionSlice";
import { userLoggedOut } from "../store/auth/userSlice";
import { Dialog } from "components/ui";

/**
 * Helper: call system-maintenance endpoint and extract best message
 * Endpoint used: GET /v3/exter/system-maintenance?purpose=maintenance
 * Returns: string message OR null
 */

/**
 * Helper: call system-maintenance endpoint and extract best message
 * Endpoint used: GET /v3/exter/system-maintenance?purpose=maintenance
 * Returns: string message OR null
 */
async function fetchMaintenanceNotice() {
  try {
    const resp = await BaseService.get("/v3/exter/system-maintenance", {
      params: { purpose: "maintenance" },
      // small timeout already handled by BaseService config
    });

    const body = resp?.data;
    if (!body) return null;

    const data = body.data ?? body; // support both { data: [...] } and raw
    if (!data) return null;

    // If data is a string like "no record found" treat as no message
    if (typeof data === "string") {
      const trimmed = data.trim();
      if (!trimmed || trimmed.toLowerCase().includes("no record")) return null;
      return trimmed;
    }

    // If data is an array, prefer the first item's message/title/notice/description
    if (Array.isArray(data) && data.length > 0) {
      const first = data[0] || {};
      if (typeof first === "string") {
        const t = first.trim();
        if (t && !t.toLowerCase().includes("no record")) return t;
        return null;
      }
      return (
        first.message ||
        first.notice ||
        first.description ||
        first.title ||
        // fallback to JSON so developer can at least see something
        JSON.stringify(first)
      );
    }

    // If data is an object, attempt to extract sensible fields
    if (typeof data === "object") {
      return (
        data.message ||
        data.notice ||
        data.description ||
        data.title ||
        (data.data && (data.data.message || data.data.title)) ||
        JSON.stringify(data)
      );
    }

    return null;
  } catch (err) {
    console.error("fetchMaintenanceNotice failed", err);
    return null;
  }
}

/**
 * MaintenanceController
 * - Shows modal when maintenance detected
 * - As soon as modal opens (either from persisted flag at boot, or app:maintenance event),
 *   it immediately calls /v3/exter/system-maintenance to fetch the real message and shows it.
 * - Retry button still re-checks same endpoint and behaves as before.
 */
const MaintenanceController = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  // helper to set message and persist
  const applyMessage = (msg) => {
    const final = msg || "The system is under maintenance. Please try later.";
    setMessage(final);
    try {
      localStorage.setItem("app_under_maintenance_message", final);
      localStorage.setItem("app_under_maintenance", "true");
    } catch (e) {
      // ignore storage errors
    }
  };

  // Immediately fetch maintenance message when modal opens.
  // This helper will be used both on event and on boot when persisted flag found.
  const fetchAndApplyNotice = async (fallbackMessage) => {
    try {
      // show loading while we fetch real message
      setMessage("Loading maintenance details...");
      const remoteMsg = await fetchMaintenanceNotice();
      if (remoteMsg) {
        applyMessage(remoteMsg);
        return true;
      }

      // if remote msg not found, use fallback from sysMode or provided fallback
      if (fallbackMessage) {
        applyMessage(fallbackMessage);
        return false;
      }

      // final fallback
      applyMessage("The system is under maintenance. Please try later.");
      return false;
    } catch (err) {
      console.error("fetchAndApplyNotice failed", err);
      applyMessage(
        fallbackMessage || "The system is under maintenance. Please try later."
      );
      return false;
    }
  };

  // Try to read cached message + open flag at boot.
  // If persisted flag exists we will open the modal but *still call the API immediately*
  useEffect(() => {
    try {
      const persisted = localStorage.getItem("app_under_maintenance");
      if (persisted === "true") {
        // open modal but don't show stale cached message; show loader and fetch real message
        setOpen(true);
        // ensure auth cleared
        try {
          store.dispatch(onSignOutSuccess());
          store.dispatch(userLoggedOut());
        } catch (e) {
          console.error("dispatch signout failed", e);
        }

        // attempt to fetch latest message and apply
        // we purposely do not `await` here to avoid blocking UI thread
        (async () => {
          // try fetch; if fails we'll fall back to cached message so user sees something
          const cached = localStorage.getItem("app_under_maintenance_message");
          await fetchAndApplyNotice(
            cached || "The system is under maintenance."
          );
        })();
      }
    } catch (e) {
      console.error("maintenance persisted check failed", e);
    }

    // handler invoked by interceptor
    const handler = async (e) => {
      const sysMode = e?.detail?.systemMode || {};
      // fallback derived from sysMode if present
      const fallback = sysMode.message || "The system is under maintenance.";

      // immediately open modal, but show loading & fetch real message
      setOpen(true);

      // clear auth state
      try {
        store.dispatch(onSignOutSuccess());
        store.dispatch(userLoggedOut());
      } catch (err) {
        console.error("dispatch signout failed", err);
      }

      // Immediately fetch the authoritative maintenance message and apply it
      await fetchAndApplyNotice(fallback);
    };

    window.addEventListener("app:maintenance", handler);
    return () => window.removeEventListener("app:maintenance", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close modal: clear persisted maintenance flag so user can try to login later
  const handleClose = () => {
    try {
      localStorage.removeItem("app_under_maintenance");
      localStorage.removeItem("app_under_maintenance_message");
    } catch (e) {}
    setOpen(false);
    // user remains signed out (onSignOutSuccess already dispatched)
  };

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      onRequestClose={handleClose}
      to="/under-maintenance"
    >
      <div className="px-6">
        <h5 className="mb-4">Maintenance break</h5>
        <p>{message || "Loading maintenance details..."}</p>
      </div>
    </Dialog>
  );
};

export default MaintenanceController;
